/**
 * Shared Custom Size logic for dress PDPs (unit switch, fields, validation, ERP size).
 * Single owner — do not re-bind from product-family-ajax or buy-buttons.
 */
(function () {
  if (window.ProductCustomSize) return;

  class ProductCustomSize {
    constructor() {
      this.sectionSelector = '[data-main-product-section]';
      this.bindEvents();
      this.init(document);
    }

    getSection(el) {
      if (!el || !el.closest) return document;
      return el.closest(this.sectionSelector) || document;
    }

    getContainer(root = document) {
      return root && root.querySelector ? root : document;
    }

    getProductForm(root = document) {
      const container = this.getContainer(root);
      return (
        container.querySelector('form[data-type="add-to-cart-form"]') ||
        container.querySelector('form[action="/cart/add"]') ||
        document.querySelector('form[data-type="add-to-cart-form"]') ||
        document.querySelector('form[action="/cart/add"]')
      );
    }

    isCustomSizeSelected(root = document) {
      const container = this.getContainer(root);
      return !!container.querySelector(
        'input[data-custom-size-option="true"]:checked, input[name="properties[Size]"][value*="Custom Size"]:checked'
      );
    }

    setFormProperty(form, name, value) {
      if (!form || !name) return;

      let input = Array.from(form.querySelectorAll('input[type="hidden"]')).find((item) => {
        return item.getAttribute('name') === name;
      });

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }

      input.value = value;
    }

    clearCustomSizeProperties(root = document) {
      const container = this.getContainer(root);
      const form = this.getProductForm(container);
      if (!form) return;

      [
        'properties[Unit]',
        'properties[Bust]',
        'properties[Waist]',
        'properties[Under Bust]',
        'properties[Around Belly]',
        'properties[Hips]',
        'properties[Hollow to Floor]',
        'properties[Height (Bare Foot)]',
        'properties[Extra Length]'
      ].forEach((name) => {
        const input = Array.from(form.querySelectorAll('input[type="hidden"]')).find((item) => {
          return item.getAttribute('name') === name;
        });
        if (input) input.remove();
      });

      container.querySelectorAll('.is-error').forEach((item) => {
        item.classList.remove('is-error');
      });

      const errorBox = container.querySelector('[data-error-message]');
      if (errorBox) errorBox.innerHTML = '';

      const notice = container.querySelector('.return_support_notice, .return_support_notice_inner');
      if (notice) notice.classList.remove('is-error');
    }

    updateUnitLabels(customBox, unit) {
      if (!customBox) return;
      const unitLabel = unit === 'cm' ? '(cm)' : '(in)';

      customBox.querySelectorAll('.unit_item').forEach((item) => {
        const textNode = Array.from(item.childNodes).find(
          (node) => node.nodeType === Node.TEXT_NODE && /\((in|cm)\)/.test(node.nodeValue || '')
        );
        if (textNode) textNode.nodeValue = unitLabel;
      });
    }

    rebuildCustomSizeSelects(customBox, unit) {
      if (!customBox) return;

      customBox.querySelectorAll('.custom-size-select, .dress_ul select').forEach((select) => {
        const valuesRaw =
          unit === 'cm'
            ? select.getAttribute('data-cm-values')
            : select.getAttribute('data-inch-values');

        if (!valuesRaw) {
          console.warn('Missing custom size values:', unit, select);
          return;
        }

        const values = valuesRaw
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

        select.innerHTML = '';

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = '-- Please Select --';
        select.appendChild(placeholder);

        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });

        select.value = '';
        select.classList.remove('is-error');
      });
    }

    syncMeasurePreview(root = document) {
      const scope = root && root.querySelectorAll ? root : document;
      const pickers = [];

      if (scope.matches && scope.matches('[data-product-size-picker]')) {
        pickers.push(scope);
      } else {
        scope.querySelectorAll('[data-product-size-picker]').forEach((picker) => {
          pickers.push(picker);
        });
      }

      pickers.forEach((picker) => this._syncOneMeasurePreview(picker));
    }

    _syncOneMeasurePreview(picker) {
      if (!picker) return;
      const preview = picker.querySelector('[data-size-measure-preview]');
      if (!preview) return;

      const checked = picker.querySelector('.size-item-list input[type="radio"]:checked');
      const isCustom =
        !checked ||
        checked.hasAttribute('data-custom-size-option') ||
        /custom size/i.test(checked.value || '');

      if (isCustom) {
        preview.style.display = 'none';
        return;
      }

      preview.style.display = '';
      const erp = checked.getAttribute('data-erp-size-value') || checked.value || '';
      const optionVal = checked.value || '';
      const sizeCode = optionVal.replace(/\s+/g, '');

      preview.querySelectorAll('tbody tr').forEach((tr) => {
        const match =
          (erp && tr.getAttribute('data-erp') === erp) ||
          tr.getAttribute('data-option') === optionVal ||
          tr.getAttribute('data-option-alt') === optionVal ||
          tr.getAttribute('data-size-code') === sizeCode;

        tr.style.display = match ? 'table-row' : 'none';
        tr.classList.toggle('active', Boolean(match));
      });
    }

    init(root = document) {
      this.initCustomSizeUnit(root);
      this.toggleCustomSizeFields(root);
      this.syncErpSizeProperty(root);
      this.syncMeasurePreview(root);
    }

    initCustomSizeUnit(root = document) {
      const container = this.getContainer(root);
      const customBox = container.querySelector('[data-custom-size-box]');
      if (!customBox) return;

      const switchBtn = customBox.querySelector('.unit-switch');
      const currentUnit = switchBtn && switchBtn.classList.contains('right') ? 'cm' : 'inch';

      this.rebuildCustomSizeSelects(customBox, currentUnit);
      this.updateUnitLabels(customBox, currentUnit);
    }

    toggleCustomSizeFields(root = document) {
      const container = this.getContainer(root);
      const customBox = container.querySelector('[data-custom-size-box]');
      if (!customBox) return;

      if (this.isCustomSizeSelected(container)) {
        customBox.style.display = 'block';
      } else {
        customBox.style.display = 'none';
        this.clearCustomSizeProperties(container);
      }
    }

    toggleCustomUnit(switchBtn, root = document) {
      const customBox = switchBtn.closest('[data-custom-size-box]');
      if (!customBox) return;

      const currentUnit = switchBtn.classList.contains('right') ? 'cm' : 'inch';
      const nextUnit = currentUnit === 'inch' ? 'cm' : 'inch';

      switchBtn.classList.toggle('left', nextUnit === 'inch');
      switchBtn.classList.toggle('right', nextUnit === 'cm');
      switchBtn.setAttribute('data-current-unit', nextUnit);

      this.rebuildCustomSizeSelects(customBox, nextUnit);
      this.updateUnitLabels(customBox, nextUnit);

      const errorBox = customBox.querySelector('[data-error-message]');
      if (errorBox) errorBox.innerHTML = '';

      this.clearCustomSizeProperties(root);
    }

    syncErpSizeProperty(root = document) {
      const container = this.getContainer(root);
      const erpInput = container.querySelector('[data-erp-size-input]');
      if (!erpInput) return;

      if (this.isCustomSizeSelected(container)) {
        erpInput.value = 'Custom Size';
        return;
      }

      const checkedSizeInput = container.querySelector('input[data-erp-size-value]:checked');
      if (checkedSizeInput) {
        erpInput.value = checkedSizeInput.getAttribute('data-erp-size-value') || '';
        return;
      }

      const sizeSelect = Array.from(container.querySelectorAll('select[data-option-name]')).find((select) => {
        const normalized = String(select.getAttribute('data-option-name') || '')
          .toLowerCase()
          .trim()
          .replace(/ß/g, 'ss')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ä/g, 'ae')
          .replace(/ñ/g, 'n');
        return (
          normalized === 'size' ||
          normalized === 'grosse' ||
          normalized === 'groesse' ||
          normalized === 'taille' ||
          normalized === 'taglia' ||
          normalized === 'tamano'
        );
      });

      if (sizeSelect && sizeSelect.selectedOptions && sizeSelect.selectedOptions[0]) {
        erpInput.value = sizeSelect.selectedOptions[0].getAttribute('data-erp-size-value') || '';
        return;
      }

      erpInput.value = '';
    }

    validateAndGenerateCustomSize(root = document) {
      const container = this.getContainer(root);
      const form = this.getProductForm(container);
      const customBox = container.querySelector('[data-custom-size-box]');
      const errorBox = container.querySelector('[data-error-message]');

      if (!form || !customBox) return true;

      if (!this.isCustomSizeSelected(container)) {
        this.clearCustomSizeProperties(container);
        return true;
      }

      const missing = [];
      const switchBtn = customBox.querySelector('.unit-switch');
      const currentUnit = switchBtn && switchBtn.classList.contains('right') ? 'cm' : 'inch';

      this.setFormProperty(form, 'properties[Unit]', currentUnit);
      this.setFormProperty(form, 'properties[_Size]', 'Custom Size');

      customBox.querySelectorAll('.dress_ul select, .custom-size-select').forEach((select) => {
        const value = select.value ? select.value.trim() : '';
        const optionName = select.getAttribute('data-option') || 'Measurement';
        const propertyName = select.getAttribute('data-name');
        if (!propertyName) return;

        if (!value) {
          missing.push(optionName);
          select.classList.add('is-error');
        } else {
          select.classList.remove('is-error');
          this.setFormProperty(form, propertyName, value);
        }
      });

      const checkbox = customBox.querySelector('[name="return_support_checkbox"]');
      const notice = customBox.querySelector('.return_support_notice, .return_support_notice_inner');

      if (!checkbox || !checkbox.checked) {
        missing.push('terms and conditions');
        if (notice) notice.classList.add('is-error');
      } else if (notice) {
        notice.classList.remove('is-error');
      }

      if (missing.length) {
        if (errorBox) {
          errorBox.innerHTML = `<span class="error">Please provide: ${missing.join(', ')}</span>`;
        }
        return false;
      }

      if (errorBox) errorBox.innerHTML = '';
      return true;
    }

    bindEvents() {
      if (window.__dunteryCustomSizeBound) return;
      window.__dunteryCustomSizeBound = true;

      document.addEventListener('click', (event) => {
        const switchBtn = event.target.closest('[data-custom-size-box] .unit-switch');
        if (!switchBtn) return;
        this.toggleCustomUnit(switchBtn, this.getSection(switchBtn));
      });

      document.addEventListener('focusin', (event) => {
        const field = event.target.closest('[data-custom-size-box] select, [data-custom-size-box] input');
        if (!field) return;

        const label = field.closest('label');
        const desc = label ? label.getAttribute('data-description') : '';
        const optionName = field.getAttribute('data-option');
        const section = this.getSection(field);
        const descBox = section.querySelector('[data-customize-option-desc]');

        if (descBox && optionName && desc) {
          descBox.innerHTML = `<span>${optionName}<span class="required">*</span>: </span>${desc}`;
        }
      });

      document.addEventListener('change', (event) => {
        const input = event.target.closest(
          'input[data-custom-size-option], input[data-erp-size-value], input[name="properties[Size]"], input[name="Size"], select[data-option-name], [data-custom-size-box] select, [data-product-size-picker] .size-item-list input[type="radio"]'
        );
        if (!input) return;

        const section = this.getSection(input);
        this.toggleCustomSizeFields(section);
        this.syncErpSizeProperty(section);
        this.syncMeasurePreview(input.closest('[data-product-size-picker]') || section);

        const errorBox = section.querySelector('[data-error-message]');
        if (errorBox) errorBox.innerHTML = '';
        input.classList.remove('is-error');
      });

      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target.closest('form[data-type="add-to-cart-form"], form[action="/cart/add"]');
          if (!form) return;

          const section = this.getSection(form);
          this.syncErpSizeProperty(section);

          if (!this.validateAndGenerateCustomSize(section)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return false;
          }
        },
        true
      );

      document.addEventListener('product:ajax-loaded', (event) => {
        const section = event.detail && event.detail.section;
        if (section) this.init(section);
      });
    }
  }

  window.ProductCustomSize = ProductCustomSize;

  const boot = () => {
    if (!window.productCustomSize) {
      window.productCustomSize = new ProductCustomSize();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
